import { useState } from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useFarmSettingsContext } from '../../contexts/FarmSettingsContext';
import {
  ArchiveFarmMutation,
  DeleteFarmMutation,
  DuplicateFarmMutation,
  LockFarmMutation,
  UnarchiveFarmMutation,
  UnlockFarmMutation,
} from '../../mutations';
import DotMenuIcon from '../icons/DotMenuIcon';
import { useUser, useUserRole } from '../../utils/hooks';
import { captureEvent } from '../../utils/analytics';
import ConfirmationModal from '../basics/ConfirmationModal';
import { farmCanBeDuplicated } from '../../utils/utils';

const StyledDropdownToggle = styled(DropdownToggle)`
  margin-top: -4px;
`;

const UnderlinedLink = styled(Link)`
  text-decoration: underline;
`;

const FarmActionsDropdown = ({ farm, mutateAndSetState, refetchQuery }) => {
  const location = useLocation();
  const onSharedWithMePage = location.pathname.includes('/app/shared-projects');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((open) => !open);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(
    false,
  );

  const { openSettingsModalForFarm } = useFarmSettingsContext();

  const [duplicateFarm, duplicateFarmMutationResult] = useMutation(
    DuplicateFarmMutation,
    {
      refetchQueries: [
        // the team id will not be used by all refetch queries, but include just in case
        { query: refetchQuery, variables: { id: farm.team?.id } },
      ],
    },
  );

  const [archiveOrUnarchiveMutation] = useMutation(
    farm.isArchived ? UnarchiveFarmMutation : ArchiveFarmMutation,
    {
      refetchQueries: [
        // teamId will not be used by all refetch queries, but include just in case
        { query: refetchQuery, variables: { id: farm.team?.id } },
      ],
    },
  );

  const user = useUser();
  const isFarmOwner = farm.owner?.id === user.id;
  const [lockOrUnlockMutation] = useMutation(
    farm.isLocked ? UnlockFarmMutation : LockFarmMutation,
    {
      refetchQueries: [
        // teamId will not be used by all refetch queries, but include just in case
        { query: refetchQuery, variables: { id: farm.team?.id } },
      ],
    },
  );

  const [deleteFarm] = useMutation(DeleteFarmMutation, {
    refetchQueries: [
      // teamId will not be used by all refetch queries, but include just in case
      { query: refetchQuery, variables: { id: farm.team?.id } },
    ],
  });

  const { loading } = duplicateFarmMutationResult;

  // only superadmins or farms with valid subscriptions can be copied
  const { isSuperadmin } = useUserRole();
  const allowDuplication = farmCanBeDuplicated(farm, isSuperadmin);

  const canEdit = farm?.allowedActions?.canEdit;
  if (!canEdit) return null;

  /**
   * A project copied from "shared with me" will not show up on the "shared with
   * me" page, so we add aditional context in this success message to make
   * clearer where it went.
   */
  const renderSuccessMsg = (result) => (
    <div>
      <UnderlinedLink to={`/app/farm/${result?.id}/fields`}>
        {result?.name}
      </UnderlinedLink>{' '}
      successfully copied {onSharedWithMePage ? 'to My Projects' : ''}
    </div>
  );

  return (
    <Dropdown
      id={`farm-action-menu-${farm.id}`}
      isOpen={dropdownOpen}
      toggle={toggle}
      className="flex-shrink-0"
      onClick={(e) => e.stopPropagation()}
    >
      <ConfirmationModal
        isOpen={isConfirmDeleteModalOpen}
        onCancel={() => setIsConfirmDeleteModalOpen(false)}
        onConfirm={() => {
          captureEvent('farm-deleted', {
            location: 'project table dropdown',
          });
          mutateAndSetState(
            () =>
              deleteFarm({
                variables: {
                  input: { id: farm.id, updatedAt: farm.updatedAt },
                },
              }),
            `Deleted ${farm.name} successfully`,
          );
        }}
        title="Delete farm?"
        actionQuestion="Are you sure you want to delete this project permanently?"
        actionName="Delete"
      />
      <StyledDropdownToggle className="px-1 py-0" color="link">
        <DotMenuIcon />
      </StyledDropdownToggle>
      <DropdownMenu positionFixed>
        <DropdownItem
          onClick={() => {
            openSettingsModalForFarm(farm.id, refetchQuery);
          }}
        >
          Project Settings
        </DropdownItem>
        <DropdownItem
          onClick={() =>
            mutateAndSetState(
              () => duplicateFarm({ variables: { farmId: farm.id } }),
              renderSuccessMsg,
            )
          }
          disabled={loading || !allowDuplication}
        >
          {loading ? 'Duplicating...' : 'Duplicate Farm'}
        </DropdownItem>
        {isFarmOwner && !farm.isArchived && (
          <DropdownItem
            onClick={() => {
              captureEvent(farm.isLocked ? 'farm-unlocked' : 'farm-locked', {
                location: 'project table dropdown',
              });
              mutateAndSetState(
                () =>
                  lockOrUnlockMutation({
                    variables: {
                      input: { id: farm.id, updatedAt: farm.updatedAt },
                    },
                  }),
                farm.isLocked
                  ? `Unlocked ${farm.name} successfully`
                  : `Locked ${farm.name} successfully`,
              );
            }}
            disabled={loading}
          >
            {farm.isLocked ? 'Unlock Farm' : 'Lock Farm'}
          </DropdownItem>
        )}
        {!farm.isArchived && <DropdownItem divider />}
        <DropdownItem
          onClick={() => {
            captureEvent(
              farm.isArchived ? 'farm-unarchived' : 'farm-archived',
              { location: 'project table dropdown' },
            );
            mutateAndSetState(
              () =>
                archiveOrUnarchiveMutation({
                  variables: {
                    input: { id: farm.id, updatedAt: farm.updatedAt },
                  },
                }),
              farm.isArchived
                ? `Restored ${farm.name} successfully`
                : `Archived ${farm.name} successfully`,
            );
          }}
          disabled={loading}
        >
          {farm.isArchived ? 'Restore' : 'Archive'} Farm
        </DropdownItem>
        {farm.isArchived && (
          <>
            <DropdownItem divider />
            <DropdownItem
              onClick={() => setIsConfirmDeleteModalOpen(true)}
              disabled={loading}
            >
              Delete farm
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

export default FarmActionsDropdown;
